import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {Box, IconButton, Tooltip, Typography} from '@mui/material';
import {useRef} from 'react';
import ChartExportMenu, {EXPORT_IGNORE} from './ChartExportMenu';
import VerticalBarChart from './VerticalBarChart';

export interface MultiIndicatorItem {
    label: string;
    uoa: number | null;
    average: number | null;
}

interface MultiIndicatorSeriesLabels {
    uoa: string;
    average: string;
}

interface MultiIndicatorProps {
    title: string;
    tooltip: string;
    items: MultiIndicatorItem[];
    isCount?: boolean;
    maxOverride?: number;
    barHeight?: number;
    seriesLabels?: MultiIndicatorSeriesLabels;
    stacked?: boolean;
}

const MultiIndicator = ({
    title,
    tooltip,
    items,
    isCount,
    maxOverride,
    barHeight = 160,
    seriesLabels = {uoa: 'Il mio progetto', average: 'Media dei progetti'},
    stacked = false,
}: MultiIndicatorProps) => {
    const chartRef = useRef<HTMLDivElement>(null);

    return (
        <Box ref={chartRef} sx={{mb: 2.5}}>
            <Box
                sx={{display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 1.5}}
            >
                <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{flex: 1, lineHeight: 1.45}}
                >
                    {title}
                </Typography>
                <Tooltip title={tooltip} placement='top' arrow>
                    <IconButton
                        {...EXPORT_IGNORE}
                        size='small'
                        sx={{p: '2px', color: 'text.disabled', flexShrink: 0}}
                    >
                        <InfoOutlinedIcon sx={{fontSize: 15}} />
                    </IconButton>
                </Tooltip>
                <ChartExportMenu target={chartRef} title={title} />
            </Box>

            {stacked ? (
                <VerticalBarChart
                    groups={[
                        {
                            label: seriesLabels.uoa,
                            values: items.map((item) => item.uoa),
                            isCount,
                            maxOverride,
                        },
                        {
                            label: seriesLabels.average,
                            values: items.map((item) => item.average),
                            isCount,
                            maxOverride,
                        },
                    ]}
                    series={items.map((item) => ({label: item.label}))}
                    barHeight={barHeight}
                    sharedMax
                    showLegend
                    stacked
                />
            ) : (
                <VerticalBarChart
                    groups={items.map((item) => ({
                        label: item.label,
                        values: [item.uoa, item.average],
                        isCount,
                        maxOverride,
                    }))}
                    series={[{label: seriesLabels.uoa}, {label: seriesLabels.average}]}
                    barHeight={barHeight}
                    sharedMax
                    showLegend
                />
            )}
        </Box>
    );
};

export default MultiIndicator;
